import { Box, Text, StyleProps, VStack } from "@chakra-ui/react" ;
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react" ;

import { $count, $length, $ok } from "foundation-ts/commons";
import { $ascii, $trim } from "foundation-ts/strings";
import { Nullable, UUID } from "foundation-ts/types";
import { UserDto } from "doxecure-commons/doxecureClientTypes";
import { compareUserBodies } from "doxecure-commons/doxecureFunctions";

import { UserCard } from "@components/Cards/UserCard";
import { SearchField } from "@components/SearchField/SearchField";
import { SectionBox } from "@components/SectionBox/SectionBox";
import { UserParameters } from "@components/UserInfos/UserInfos";
import { useStyles } from "@hooks/useStyles";
import { SectionUsers } from "@utils/ReactTypesAndConstants";

export interface SearchUsersProperties extends StyleProps {
    users: UserParameters[];
    isAdminCorporate?: boolean;
    displayIcon?: boolean;
    displayOrganizationName?: boolean;
    displayRole?: boolean;
    displayReachedLimitOfUsers?: Nullable<JSX.Element>;
    onSearch: (search?: Nullable<string>) => Promise<void>;
    onItemClick?: (userId: UUID) => void;
} ;

export const SearchUsers = (props: SearchUsersProperties) => {
    const {users, isAdminCorporate, displayIcon, displayOrganizationName, displayRole, displayReachedLimitOfUsers, onSearch, onItemClick} = props ;
    const { sectionStyle, itemStyle } = useStyles();
    const [searchValue, setSearchValue] = useState<string>("") ;
    const searchFormRef = useRef<HTMLFormElement>(null) ;
    const usersSections = $count(users) ? _createSectionList(users).sort(_compareSection) : [] ;

    const handleSearch = async (e: FormEvent) => {
        e.preventDefault();
        if (e.type === "submit") {
            if ($length(e.target[0].value) === 0) {
                await onSearch() ;
            } else {
                await onSearch(e.target[0].value) ;
            }
        }
    }

    const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value) ;
    } ;

    const renderUserBox = (item: UserParameters) => {
        return <UserCard key={item.apid} item={item} itemStyle={itemStyle.user} displayIcon={displayIcon} displayOrganizationName={displayOrganizationName} displayRole={displayRole} isAdminCorporate={isAdminCorporate} onItemClick={$ok(onItemClick) ? () => onItemClick!(item.apid!) : undefined} /> ;
    }

    useEffect(() => {
        if (searchValue.length === 0) {
            searchFormRef.current?.requestSubmit() ;
        }
    }, [searchValue]) ;

    return (
        <>
            {displayReachedLimitOfUsers}
        
            <Box mb="6">
                <form ref={searchFormRef} onSubmit={handleSearch}>
                    <SearchField component="form" h="30px" placeHolder="Nom ou Adresse mail" onChange={handleSearchInputChange} />
                </form>
            </Box>
            <VStack align="stretch" spacing="5">
            {usersSections.map((section, index) => {
                return (
                    <Box width="100%" key={index}>
                        <Text fontWeight="bold" marginBottom="10px">{section.letter}</Text>
                        <SectionBox items={section.items} sectionStyle={sectionStyle.users} renderItem={renderUserBox} />
                    </Box>
                );
            })}
            </VStack>
        </>
    ) ;
} ;

const _compareUsers = (a: UserDto, b: UserDto) => {
    return compareUserBodies(a, b) ;
};

const _compareSection = (a: SectionUsers, b: SectionUsers) => {
    if (a.letter < b.letter) {
        return -1 ;
    } else if (a.letter === b.letter) {
        return 0 ;
    } else {
        return 1 ;
    }
};

const _createSectionList = (users:UserDto[]): SectionUsers[] => {
    const sections:SectionUsers[] = [] ;
    for (let i = 0 ; i < 26 ; i++) {
        sections[i] = { items:[], letter:String.fromCharCode(65+i) } ;
    } 
    sections[26] = { items:[], letter:'?'} ;

    const sortedUsers = users.sort(_compareUsers) ;
    sortedUsers.forEach(c => {
        const name = $trim(c.lastName)+$trim(c.firstName) ;
        if (name.length) {
            let index = $ascii(name.charAt(0)).toUpperCase().charCodeAt(0)-65 ;
            if (index < 0 || index >= 26) { index = 26 ; }
            sections[index].items.push(c) ;
        }
    }) ;
    return sections.filter(s => s.items.length > 0) ;
} ;